import { useContext, useMemo, useState } from "react";
import UiContext from "../../../store/ui-context";
import useAPIRequest from "../../../custom_hooks/simple/useAPIRequest";
import {
  apiUrl,
  fetchOrderBy,
  fetchOrderMethod,
  fetchTake,
  initialPaging,
  problemStatusTabs,
  solvedStatus,
} from "../utils/problemConstants";

// the filter of the page: field of the filter -> field of the problem
const filterFields = {
  kendaraan: "data_kendaraan.nomor_polisi",
  trailer: "data_trailer.nomor",
  keterangan: "keterangan",
  label_grup: "label_grup",
  di_tangani: "data_di_tangani.nama",
};

const defaultFilterValue = {
  kendaraan: "",
  trailer: "",
  keterangan: "",
  label_grup: "",
  di_tangani: "",
};

const getFilter = (filterValue) =>
  Object.keys(filterFields)
    .filter((key) => filterValue[key])
    .map((key) => `${filterFields[key]}:contains:${filterValue[key]}`)
    .join(";");

// a problem that is solved: status solved or it has a tanggal selesai
const isSolved = (el) => el.status === solvedStatus || !!el.tanggal_selesai;

// The problems of the page, pulled with TWO requests (take 10000), each one only when it is needed:
//   - problems that are not solved (tabs baru, schedule and ignore): pulled when the page opens
//   - problems that are solved (tab solved: status solved or has a tanggal selesai): pulled the first time the tab is opened
// After that everything is done locally, no request until the data is refreshed (refresh button):
//   - tab (status of the problem): 1 baru, 2 schedule, 3 solved, 4 ignore
//   - inside a tab: listData, the one list of the tab
//   - paging: { first, rows } of the table
// null = not pulled yet
// config: { jenis } of the variant
const useProblemList = ({ config }) => {
  const uiContext = useContext(UiContext);
  const { requestGet, requestGetOne, requestDelete, loading } = useAPIRequest();

  const [unsolvedData, setUnsolvedData] = useState(null);
  const [solvedData, setSolvedData] = useState(null);
  const [status, setStatus] = useState(problemStatusTabs[0].status);
  // the filter fields (sent to the server when the data is pulled)
  const [filterValue, setFilterValue] = useState(defaultFilterValue);

  const [paging, setPaging] = useState(initialPaging);
  const [selectedData, setSelectedData] = useState([]);

  // the list of the active tab
  const listData = useMemo(() => {
    const inTab =
      status === solvedStatus
        ? solvedData ?? []
        : (unsolvedData ?? []).filter((el) => (el.status ?? 1) === status);

    return inTab.map((el, index) => ({ ...el, no: index + 1 }));
  }, [unsolvedData, solvedData, status]);

  // how many problems each status has (only known once the not solved problems are pulled)
  const statusCounts = useMemo(() => {
    const counts = {};
    (unsolvedData ?? []).forEach((el) => {
      const key = el.status ?? 1;
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return counts;
  }, [unsolvedData]);

  // API call section

  // solved: the problems that are solved, otherwise the ones that are not (of this jenis, with the filter of the user)
  const fetchProblems = async ({ solved }) => {
    const filter = [
      getFilter(filterValue),
      `jenis:=:${config.jenis}`,
      solved
        ? `status:=:${solvedStatus}||tanggal_selesai:!=:Null`
        : `status:!=:${solvedStatus};tanggal_selesai:=:Null`,
    ]
      .filter((el) => el.length > 0)
      .join(";");

    return requestGet({
      fullUrl: `/api/problem/${apiUrl}/data`,
      params: {
        page: 1,
        take: fetchTake,
        filter,
        order: fetchOrderBy,
        order_method: fetchOrderMethod,
        with_kendaraan: 1,
        with_trailer: 1,
        // the jenis mobil of the vehicle / trailer: its photos are the Model Referensi of the image preview
        "with_kendaraan,data_jenis_mobils": 1,
        "with_trailer,data_jenis_mobils": 1,
        with_di_tangani: 1,
        "with_pengecekan_mobil_detil,data_pengecekan_mobil": 1,
        "with_audit_kendaraan_detil,data_audit_kendaraan": 1,
      },
      onSuccess: ({ pagination, message, data }) => {
        if (solved) {
          setSolvedData(data);
        } else {
          setUnsolvedData(data);
        }
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const fetchData = async ({ id }) => {
    if (loading) return;
    return requestGetOne({
      fullUrl: `/api/problem/${apiUrl}/one/${id}`,
      onSuccess: ({ message, data }) => {},
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  const deleteData = async ({ ids }) => {
    if (loading) return;

    return requestDelete({
      fullUrl: `/api/problem/${apiUrl}/delete`,
      ids: ids,
      onSuccess: ({ message, data }) => {
        const notDeleted = (prev) =>
          prev && prev.filter((el) => !ids.includes(el.id));
        setUnsolvedData(notDeleted);
        setSolvedData(notDeleted);

        uiContext.showSuccessMsg("Success", message);
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  // local changes after the editor saved (no request)
  // The problem goes to the list it belongs to now: a problem that became solved leaves the unsolved list and the other way around.

  const putProblem = (list, newData) => {
    if (!list) return list;
    const index = list.findIndex((el) => el.id === newData.id);
    if (index < 0) return [newData, ...list];
    const newList = [...list];
    newList.splice(index, 1, newData);
    return newList;
  };

  const removeProblem = (list, newData) =>
    list && list.filter((el) => el.id !== newData.id);

  const addProblem = (newData) => {
    setUnsolvedData((prev) => (prev ? [newData, ...prev] : prev));
  };

  const replaceProblem = (newData) => {
    if (isSolved(newData)) {
      setUnsolvedData((prev) => removeProblem(prev, newData));
      setSolvedData((prev) => putProblem(prev, newData));
    } else {
      setSolvedData((prev) => removeProblem(prev, newData));
      setUnsolvedData((prev) => putProblem(prev, newData));
    }
  };

  // handle UI events section

  const resetPaging = () => {
    setPaging({ ...paging, first: 0 });
  };

  // first load of the page
  const handleOnInitialLoad = () => {
    fetchProblems({ solved: false });
  };

  // another status tab: only start at the first page again.
  // The data of the tab is pulled the first time (solved has its own data), after that it is already here.
  const handleOnChangeStatus = (newStatus) => {
    setStatus(newStatus);
    resetPaging();

    if (newStatus === solvedStatus && solvedData === null) {
      fetchProblems({ solved: true });
    }
    if (newStatus !== solvedStatus && unsolvedData === null) {
      fetchProblems({ solved: false });
    }
  };

  // refresh: pull the data of this tab again with the filter.
  // The data of the other kind was pulled with the old filter, so it is pulled again when its tab is opened.
  const handleOnClickRefresh = () => {
    resetPaging();
    if (status === solvedStatus) {
      setUnsolvedData(null);
      fetchProblems({ solved: true });
    } else {
      setSolvedData(null);
      fetchProblems({ solved: false });
    }
  };

  // Paginator of the tables (local paging)
  const handleOnPage = (e) => {
    setPaging({ first: e.first, rows: e.rows });
  };

  return {
    loading,
    status,
    statusCounts: unsolvedData === null ? null : statusCounts,
    filterValue,
    setFilterValue,
    paging,
    listData,
    selectedData,
    setSelectedData,
    fetchData,
    deleteData,
    addProblem,
    replaceProblem,
    handleOnInitialLoad,
    handleOnChangeStatus,
    handleOnClickRefresh,
    handleOnPage,
  };
};

export default useProblemList;
